import React, { useState, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox,
  Divider,
  Skeleton,
  Paper,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  Lock,
  CreditCard,
  AccountBalance,
  Security,
  Verified,
  Info,
  Close,
  CheckCircle,
  Error as ErrorIcon,
} from '@mui/icons-material'
import {
  loadStripe,
  Stripe as StripeType,
  StripeCardElement,
} from '@stripe/stripe-js'
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { MembershipPlan } from '@/types/subscription'
import { AddressAutocomplete } from '@/components/AddressAutocomplete'
import api from '@/services/api'

// Stripe publishable key
const stripePromise = loadStripe(
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
)

const THERAPIST_CREATE_ACCOUNT_DRAFT_KEY =
  'therapist_create_account_draft_v1' as const

type PaymentMethod = 'card' | 'amazon_pay' | 'bank_account'

interface PaymentModalProps {
  open: boolean
  onClose: () => void
  onPaymentSuccess: (paymentIntentId: string) => void
  plan: MembershipPlan | null
  billingCycle: 'monthly' | 'yearly'
  amount: number
  therapistFirstName?: string
  therapistLastName?: string
  therapistEmail?: string
  clientSecret?: string // Add client_secret from subscription response
  billingAddress?: string // Billing address from form
  billingCity?: string // Billing city from form
  billingState?: string // Billing state from form
  billingZip?: string // Billing zip code from form
  isFromOnboarding?: boolean // Flag to indicate if modal is opened from onboarding flow
  isInitializing?: boolean // Flag to show loading state while API calls are in progress
  discountedPrice?: number
  agencyId?: string | null
}

interface BillingAddress {
  address1: string
  address2: string
  city: string
  state: string
  postalCode: string
  country: string
}

// Stripe card element styling
const CARD_ELEMENT_OPTIONS = {
  disableLink: true,
  hidePostalCode: true,
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      lineHeight: '24px',
    },
    invalid: {
      color: '#9e2146',
    },

  },
}

// Inner component that uses Stripe hooks
const PaymentForm: React.FC<
  Omit<PaymentModalProps, 'open'> & { onError: (error: string) => void }
> = ({
  onClose,
  onPaymentSuccess,
  plan,
  billingCycle,
  amount,
  therapistFirstName = '',
  therapistLastName = '',
  therapistEmail = '',
  clientSecret = '',
  billingAddress: billingAddressProp = '',
  billingCity: billingCityProp = '',
  billingState: billingStateProp = '',
  billingZip: billingZipProp = '',
  isFromOnboarding = false,
  isInitializing = false,
  onError,
  discountedPrice,
  agencyId = null,
}) => {
    const stripe = useStripe()
    const elements = useElements()

    // Refs for scrolling
    const paymentFormRef = useRef<HTMLDivElement>(null)
    const errorRef = useRef<HTMLDivElement>(null)

    const [selectedPaymentMethod, setSelectedPaymentMethod] =
      useState<PaymentMethod>('card')
    const [isProcessing, setIsProcessing] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState(false)
    const [completedPaymentIntentId, setCompletedPaymentIntentId] = useState<
      string | null
    >(null)
    const [saveCard, setSaveCard] = useState(false)
    const [cardholderName, setCardholderName] = useState('')
    const [cardComplete, setCardComplete] = useState(false)
    const [bankAccountDetails, setBankAccountDetails] = useState({
      accountNumber: '',
      routingNumber: '',
      accountHolderName: '',
      accountType: 'checking' as 'checking' | 'savings',
    })

    const [billingAddress, setBillingAddress] = useState<BillingAddress>({
      address1: '',
      address2: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'US',
    })

    // Auto-populate billing address from form data
    useEffect(() => {
      if (
        billingAddressProp ||
        billingCityProp ||
        billingStateProp ||
        billingZipProp
      ) {
        setBillingAddress(prev => ({
          ...prev,
          address1: billingAddressProp || prev.address1,
          city: billingCityProp || prev.city,
          state: billingStateProp || prev.state,
          postalCode: billingZipProp || prev.postalCode,
        }))
      }
    }, [billingAddressProp, billingCityProp, billingStateProp, billingZipProp])

    // Scroll to payment form when payment method changes
    useEffect(() => {
      if (paymentFormRef.current) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          const element = paymentFormRef.current
          if (element) {
            // Find the scrollable parent (DialogContent)
            const scrollableParent = element.closest(
              '[class*="MuiDialogContent-root"]'
            ) as HTMLElement
            if (scrollableParent) {
              const elementRect = element.getBoundingClientRect()
              const parentRect = scrollableParent.getBoundingClientRect()
              const scrollTop = scrollableParent.scrollTop
              const offset = 20 // Small offset from top
              const targetScrollTop =
                scrollTop + elementRect.top - parentRect.top - offset

              scrollableParent.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: 'smooth',
              })
            } else {
              // Fallback to scrollIntoView
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              })
            }
          }
        }, 150)
      }
    }, [selectedPaymentMethod])

    // Scroll to error message when error occurs
    useEffect(() => {
      if (error && errorRef.current) {
        // Small delay to ensure error is rendered
        setTimeout(() => {
          const element = errorRef.current
          if (element) {
            // Find the scrollable parent (DialogContent)
            const scrollableParent = element.closest(
              '[class*="MuiDialogContent-root"]'
            ) as HTMLElement
            if (scrollableParent) {
              const elementRect = element.getBoundingClientRect()
              const parentRect = scrollableParent.getBoundingClientRect()
              const scrollTop = scrollableParent.scrollTop
              const offset = 20 // Small offset from top
              const targetScrollTop =
                scrollTop + elementRect.top - parentRect.top - offset

              scrollableParent.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: 'smooth',
              })
            } else {
              // Fallback to scrollIntoView
              element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
              })
            }
          }
        }, 150)
      }
    }, [error])

    // Calculate plan amount based on billing cycle - moved here so it's available to all payment functions
    const planAmount =
      billingCycle === 'monthly'
        ? (plan?.monthly_fee ?? 0)
        : (plan?.anual_fee ?? 0)

    const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      setBillingAddress(prev => ({
        ...prev,
        [name]: value,
      }))
    }

    // Validation for different payment methods
    const validatePaymentData = (): boolean => {
      if (selectedPaymentMethod === 'card') {
        if (!cardholderName.trim()) {
          const errorMsg = 'Please enter cardholder name'
          setError(errorMsg)
          onError(errorMsg)
          return false
        }

        if (!cardComplete) {
          const errorMsg = 'Please enter complete card details'
          setError(errorMsg)
          onError(errorMsg)
          return false
        }

        if (!billingAddress.address1.trim()) {
          const errorMsg = 'Please enter billing address'
          setError(errorMsg)
          onError(errorMsg)
          return false
        }

        if (!billingAddress.city.trim()) {
          const errorMsg = 'Please enter city'
          setError(errorMsg)
          onError(errorMsg)
          return false
        }

        if (!billingAddress.state.trim()) {
          const errorMsg = 'Please enter state'
          setError(errorMsg)
          onError(errorMsg)
          return false
        }

        if (!billingAddress.postalCode.trim()) {
          const errorMsg = 'Please enter postal code'
          setError(errorMsg)
          onError(errorMsg)
          return false
        }
      } else if (selectedPaymentMethod === 'bank_account') {
        if (!bankAccountDetails.accountHolderName.trim()) {
          const errorMsg = 'Please enter account holder name'
          setError(errorMsg)
          onError(errorMsg)
          return false
        }

        if (
          !bankAccountDetails.routingNumber.trim() ||
          bankAccountDetails.routingNumber.length !== 9
        ) {
          const errorMsg = 'Please enter a valid 9-digit routing number'
          setError(errorMsg)
          onError(errorMsg)
          return false
        }

        if (!bankAccountDetails.accountNumber.trim()) {
          const errorMsg = 'Please enter account number'
          setError(errorMsg)
          onError(errorMsg)
          return false
        }
      }

      return true
    }

    // Handle Amazon Pay payment
    const handleAmazonPayment = async () => {
      setError(null)
      onError('')
      setIsProcessing(true)

      try {
        console.log('🔐 Initiating Amazon Pay...')

        // Step 1: Create Amazon Pay session via Stripe
        const response = await fetch('/api/create-amazon-pay-session', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: Math.round(planAmount * 100), // Convert to cents - use planAmount for correct calculation
            currency: 'usd',
            therapistEmail,
            planId: plan?._id,
            billingCycle,
          }),
        })

        if (!response.ok) {
          throw new Error('Failed to create Amazon Pay session')
        }

        const { sessionId, redirectUrl } = await response.json()

        // Step 2: Redirect to Amazon Pay
        console.log('🔄 Redirecting to Amazon Pay...')
        window.location.href = redirectUrl
      } catch (err) {
        console.error('❌ Amazon Pay error:', err)
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Amazon Pay failed. Please try again.'
        setError(errorMessage)
        onError(errorMessage)
        setIsProcessing(false)
      }
    }

    // Handle Bank Account payment
    const handleBankAccountPayment = async () => {
      setError(null)
      onError('')

      if (!validatePaymentData()) {
        return
      }

      if (!stripe) {
        const errorMsg = 'Payment system not ready. Please try again.'
        setError(errorMsg)
        onError(errorMsg)
        return
      }

      setIsProcessing(true)

      try {
        console.log('🔐 Initiating Bank Account payment...')

        // Step 1: Create payment method with bank account details
        const { error: paymentMethodError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: 'us_bank_account',
            us_bank_account: {
              routing_number: bankAccountDetails.routingNumber,
              account_number: bankAccountDetails.accountNumber,
              account_holder_type: 'individual',
              account_type: bankAccountDetails.accountType,
            },
            billing_details: {
              name: bankAccountDetails.accountHolderName,
              email: therapistEmail,
            },
          })

        if (paymentMethodError) {
          console.error('❌ Payment method creation error:', paymentMethodError)
          throw new Error(paymentMethodError.message)
        }

        console.log('✓ Bank account payment method created:', paymentMethod?.id)

        // Step 2: Create payment intent with bank account
        const response = await fetch('/api/create-bank-payment-intent', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            amount: Math.round(planAmount * 100), // Convert to cents - use planAmount for correct calculation
            currency: 'usd',
            payment_method_id: paymentMethod!.id,
            therapistEmail,
            planId: plan?._id,
            billingCycle,
            account_holder_name: bankAccountDetails.accountHolderName,
          }),
        })

        if (!response.ok) {
          throw new Error('Failed to create bank payment intent')
        }

        const { client_secret, requires_action } = await response.json()

        // Step 3: Confirm payment intent
        if (requires_action) {
          // Handle micro-deposit verification
          const { error: confirmError } =
            await stripe.confirmUsBankAccountPayment(client_secret)

          if (confirmError) {
            console.error('❌ Bank payment confirmation error:', confirmError)
            throw new Error(confirmError.message)
          }

          // Payment will be completed after micro-deposit verification
          setSuccess(true)
          setCompletedPaymentIntentId(client_secret.split('_secret_')[0])
        } else {
          // Payment completed immediately
          setSuccess(true)
          setCompletedPaymentIntentId(client_secret.split('_secret_')[0])
        }
      } catch (err) {
        console.error('❌ Bank Account payment error:', err)
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Bank payment failed. Please try again.'
        setError(errorMessage)
        onError(errorMessage)
      } finally {
        setIsProcessing(false)
      }
    }

    // Production-ready payment handler with Stripe Elements
    const handleCardPayment = async () => {
      setError(null)
      onError('')

      if (!validatePaymentData()) {
        return
      }

      if (!clientSecret) {
        const errorMsg =
          'Payment initialization failed. Please refresh and try again.'
        setError(errorMsg)
        onError(errorMsg)
        return
      }

      // Check if plan has trial days to determine intent type
      const hasTrial = plan?.trialDays && plan.trialDays > 0
      const isSetupIntent = clientSecret.startsWith('seti_')
      const isPaymentIntent = clientSecret.startsWith('pi_')

      // Validate client_secret format (accept both PaymentIntent and SetupIntent)
      if (!isPaymentIntent && !isSetupIntent) {
        const errorMsg = 'Invalid payment intent format. Please contact support.'
        console.error('❌ Invalid client_secret format:', clientSecret)
        setError(errorMsg)
        onError(errorMsg)
        return
      }

      // Validate that SetupIntent is used for trial plans and PaymentIntent for non-trial
      if (hasTrial && !isSetupIntent) {
        console.warn(
          '⚠️ Plan has trial days but received PaymentIntent. Expected SetupIntent.'
        )
      }
      if (!hasTrial && isSetupIntent) {
        console.warn(
          '⚠️ Plan has no trial days but received SetupIntent. Expected PaymentIntent.'
        )
      }

      if (!stripe || !elements) {
        const errorMsg = 'Payment system not ready. Please try again.'
        setError(errorMsg)
        onError(errorMsg)
        return
      }

      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        const errorMsg = 'Card element not found. Please refresh and try again.'
        setError(errorMsg)
        onError(errorMsg)
        return
      }

      setIsProcessing(true)

      try {
        console.log('🔐 Initiating Stripe payment...')
        console.log('Plan has trial days:', hasTrial)
        console.log(
          'Intent type:',
          isSetupIntent ? 'SetupIntent' : 'PaymentIntent'
        )
        console.log(
          'Client Secret (first 20 chars):',
          clientSecret.substring(0, 20) + '...'
        )

        // Step 1: Create Payment Method using Stripe Elements
        // This securely tokenizes the card data collected by Stripe Elements
        const { error: paymentMethodError, paymentMethod } =
          await stripe.createPaymentMethod({
            type: 'card',
            card: cardElement,
            billing_details: {
              name: cardholderName,
              email: therapistEmail,
              address: {
                line1: billingAddress.address1,
                line2: billingAddress.address2,
                city: billingAddress.city,
                state: billingAddress.state,
                postal_code: billingAddress.postalCode,
                country: billingAddress.country,
              },
            },
          })

        if (paymentMethodError) {
          console.error('❌ Payment method creation error:', paymentMethodError)
          throw new Error(paymentMethodError.message)
        }

        // console.log('✓ Payment method created:', paymentMethod?.id)

        // Step 2: Handle different intent types
        if (isSetupIntent) {
          // Handle SetupIntent (for trial periods - no immediate charge)
          console.log('🔄 Confirming card setup (trial period)...')
          const { error: confirmError, setupIntent } =
            await stripe.confirmCardSetup(clientSecret, {
              payment_method: paymentMethod!.id,
            })

          if (confirmError) {
            console.error('❌ Setup confirmation error:', confirmError)

            if (confirmError.code === 'resource_missing') {
              throw new Error(
                'Setup intent not found. This usually means the backend and frontend are using different Stripe API keys. Please verify your Stripe configuration.'
              )
            }

            throw new Error(confirmError.message)
          }

          // console.log('🔄 Stripe setup confirmation response:', setupIntent)

          // Check setup status
          if (setupIntent.status === 'succeeded') {
            console.log('✅ Setup successful! Status:', setupIntent.status)
            // For SetupIntent, use the setup intent ID (not payment intent)
            // Extract the ID from client secret: seti_xxxxx_secret_yyyyy -> seti_xxxxx
            const setupIntentId = clientSecret.split('_secret_')[0]

            // Save payment method via API
            if (paymentMethod && agencyId) {
              try {
                const payload = {
                  cardOwner: paymentMethod.billing_details?.name || therapistFirstName + ' ' + therapistLastName,
                  cardBrand: paymentMethod.card?.brand || 'visa',
                  last4: paymentMethod.card?.last4 || '',
                  expMonth: paymentMethod.card?.exp_month || 0,
                  expYear: paymentMethod.card?.exp_year || 0,
                  isDefault: false,
                  stripePaymentMethodId: paymentMethod.id,
                  agencyId: agencyId,
                }
                await api.post('/agency/payment-methods/save-paymentmethod', payload)
                console.log('Payment method saved successfully')
              } catch (pmError) {
                console.error('Failed to save payment method:', pmError)
              }
            }

            setCompletedPaymentIntentId(setupIntentId)
            setSuccess(true)
          } else if (setupIntent.status === 'requires_action') {
            console.log('🔐 Additional authentication required')
            const errorMsg =
              'Additional authentication required. Please check your card issuer.'
            setError(errorMsg)
            onError(errorMsg)
          } else {
            throw new Error(`Unexpected setup status: ${setupIntent.status}`)
          }
        } else {
          // Handle PaymentIntent (for immediate payment - no trial)
          console.log('🔄 Confirming card payment (immediate charge)...')
          const { error: confirmError, paymentIntent } =
            await stripe.confirmCardPayment(clientSecret, {
              payment_method: paymentMethod!.id,
              setup_future_usage: saveCard ? 'off_session' : undefined,
            })

          if (confirmError) {
            console.error('❌ Payment confirmation error:', confirmError)

            if (confirmError.code === 'resource_missing') {
              throw new Error(
                'Payment intent not found. This usually means the backend and frontend are using different Stripe API keys. Please verify your Stripe configuration.'
              )
            }

            throw new Error(confirmError.message)
          }

          console.log('🔄 Stripe payment confirmation response:', paymentIntent)

          // Check payment status
          if (
            paymentIntent.status === 'succeeded' ||
            paymentIntent.status === 'processing'
          ) {
            console.log('✅ Payment successful! Status:', paymentIntent.status)

            // Save payment method via API
            if (paymentMethod && agencyId) {
              try {
                const payload = {
                  cardOwner: paymentMethod.billing_details?.name || therapistFirstName + ' ' + therapistLastName,
                  cardBrand: paymentMethod.card?.brand || 'visa',
                  last4: paymentMethod.card?.last4 || '',
                  expMonth: paymentMethod.card?.exp_month || 0,
                  expYear: paymentMethod.card?.exp_year || 0,
                  isDefault: false,
                  stripePaymentMethodId: paymentMethod.id,
                  agencyId: agencyId,
                }
                await api.post('/agency/payment-methods/save-paymentmethod', payload)
                console.log('Payment method saved successfully')
              } catch (pmError) {
                console.error('Failed to save payment method:', pmError)
              }
            }

            setCompletedPaymentIntentId(paymentIntent.id)
            setSuccess(true)
          } else if (paymentIntent.status === 'requires_action') {
            console.log('🔐 Additional authentication required')
            const errorMsg =
              'Additional authentication required. Please check your card issuer.'
            setError(errorMsg)
            onError(errorMsg)
          } else {
            throw new Error(`Unexpected payment status: ${paymentIntent.status}`)
          }
        }
      } catch (err) {
        console.error('❌ Payment error:', err)
        const errorMessage =
          err instanceof Error ? err.message : 'Payment failed. Please try again.'
        setError(errorMessage)
        onError(errorMessage)
      } finally {
        setIsProcessing(false)
      }
    }

    // Main payment handler that routes to the appropriate method
    const handlePayment = async () => {
      // Clear localStorage when user clicks handlePayment

      switch (selectedPaymentMethod) {
        case 'card':
          await handleCardPayment()
          break
        case 'amazon_pay':
          await handleAmazonPayment()
          break
        case 'bank_account':
          await handleBankAccountPayment()
          break
        default:
          setError('Please select a payment method')
          onError('Please select a payment method')
      }
      localStorage.removeItem(THERAPIST_CREATE_ACCOUNT_DRAFT_KEY)
      localStorage.removeItem('providerNpi')
    }

    // planAmount is already calculated above
    const formattedPlanAmount = (discountedPrice ?? planAmount).toFixed(2)
    const displayAmount =
      billingCycle === 'yearly'
        ? `$${formattedPlanAmount}/year`
        : `$${formattedPlanAmount}/month`


    // console.log('displayAmount & discountedPrice', displayAmount, discountedPrice)

    return (
      <>
        {/* Security Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 3,
            p: 2,
            bgcolor: '#f4f2fbff',
            borderRadius: 2,
            border: '1px solid #7A6FA1',
          }}
        >
          <Security sx={{ color: '#7A6FA1', fontSize: '1.2rem' }} />
          <Typography variant="body2" sx={{ color: '#0c4a6e', fontWeight: 500 }}>
            Secured by 256-bit SSL encryption
          </Typography>
          <Verified sx={{ color: '#10b981', fontSize: '1.2rem' }} />
        </Box>

        {/* Main Layout: Left-Right Sections */}
        <Grid container spacing={3}>
          {/* Left Section: Order Summary */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                bgcolor: '#f8fafc',
                borderRadius: 3,
                p: 3,
                border: '2px solid #e2e8f0',
                position: 'sticky',
                top: 20,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: '#1e293b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CheckCircle sx={{ color: '#10b981', fontSize: '1.3rem' }} />
                Order Summary
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Typography variant="body1" sx={{ color: '#64748b' }}>
                  Plan: <strong> {plan?.name} </strong>
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {displayAmount}
                </Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Total Amount
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#7A6FA1',
                    fontSize: '1.5rem',
                  }}
                >
                  ${formattedPlanAmount}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Right Section: Payment Form */}
          <Grid item xs={12} md={8}>
            {/* Payment Method Selection */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 3,
                mb: 3,
                border: '2px solid #e2e8f0',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  mb: 3,
                  color: '#1e293b',
                }}
              >
                Choose Payment Method
              </Typography>
              <FormControl component="fieldset" fullWidth>
                <RadioGroup
                  value={selectedPaymentMethod}
                  onChange={e =>
                    setSelectedPaymentMethod(e.target.value as PaymentMethod)
                  }
                  sx={{
                    gap: 2,
                    '& .MuiRadio-root.Mui-checked': {
                      color: '#7A6FA1',
                    },
                  }}
                >
                  {/* Credit/Debit Card Option */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: '2px solid #7A6FA1',
                      borderRadius: 2,
                      bgcolor: '#f8fafc',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Radio value="card" checked />
                      <CreditCard sx={{ color: '#7A6FA1', fontSize: '1.5rem' }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Credit or Debit Card
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          Visa, Mastercard, American Express, Discover
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1}>
                        <img
                          src="https://js.stripe.com/v3/fingerprinted/img/visa-729c05c240c4bdb47b03ac81d9945bfe.svg"
                          alt="Visa"
                          style={{ height: '20px' }}
                        />
                        <img
                          src="https://js.stripe.com/v3/fingerprinted/img/mastercard-4d8844094130711885b5e41b28c9848f.svg"
                          alt="Mastercard"
                          style={{ height: '20px' }}
                        />
                        <img
                          src="https://js.stripe.com/v3/fingerprinted/img/amex-a49b82f46c5cd6a96a6e418a6ca1717c.svg"
                          alt="Amex"
                          style={{ height: '20px' }}
                        />
                      </Stack>
                    </Box>
                  </Paper>

                  {/* Amazon Pay Option - Commented out for now */}
                  {/* <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border:
                      selectedPaymentMethod === 'amazon_pay'
                        ? '2px solid #ff9900'
                        : '1px solid #e2e8f0',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#ff9900',
                      bgcolor: '#fff8f0',
                    },
                  }}
                  onClick={() => setSelectedPaymentMethod('amazon_pay')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Radio value="amazon_pay" />
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: '#ff9900',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                      }}
                    >
                      amazon
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        Amazon Pay
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Use your Amazon account for fast, secure checkout
                      </Typography>
                    </Box>
                    <Chip
                      label="Popular"
                      size="small"
                      sx={{
                        bgcolor: '#ff9900',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Paper> */}

                  {/* Bank Account Option - Commented out for now */}
                  {/* <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border:
                      selectedPaymentMethod === 'bank_account'
                        ? '2px solid #10b981'
                        : '1px solid #e2e8f0',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#10b981',
                      bgcolor: '#f0fdf4',
                    },
                  }}
                  onClick={() => setSelectedPaymentMethod('bank_account')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Radio value="bank_account" />
                    <AccountBalance sx={{ color: '#10b981', fontSize: '1.5rem' }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        US Bank Account
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Direct bank transfer - Lower fees
                      </Typography>
                    </Box>
                    <Chip
                      label="Save Money"
                      size="small"
                      sx={{
                        bgcolor: '#10b981',
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                </Paper> */}
                </RadioGroup>
              </FormControl>
            </Paper>

            {/* Success State */}
            {success && (
              <Box
                sx={{
                  mb: 3,
                  p: 3,
                  bgcolor: '#ecfdf5',
                  border: '2px solid #10b981',
                  borderRadius: 2,
                  textAlign: 'center',
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)',
                    },
                    '70%': {
                      boxShadow: '0 0 0 10px rgba(16, 185, 129, 0)',
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(16, 185, 129, 0)',
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#10b981',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.5rem',
                      fontWeight: 'bold',
                    }}
                  >
                    ✓
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      color: '#10b981',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                    }}
                  >
                    Payment successful
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#065f46',
                    fontWeight: 500,
                    fontSize: '1rem',
                  }}
                >
                  {plan?.trialDays && plan.trialDays > 0
                    ? isFromOnboarding
                      ? `Your ${plan.trialDays}-day free trial has started!`
                      : `Your ${plan.trialDays}-day free trial has started! Next, connect your bank account to receive payouts.`
                    : isFromOnboarding
                      ? 'Your subscription is now active!'
                      : 'Your subscription is now active. Next, connect your bank account to receive payouts.'}
                </Typography>
                {!isFromOnboarding && (
                  <Typography variant="body2" sx={{ color: '#059669', mt: 1 }}>
                    {plan?.trialDays && plan.trialDays > 0
                      ? 'You will be charged after the trial period ends.'
                      : 'This takes only 5 minutes.'}
                  </Typography>
                )}
              </Box>
            )}

            {/* Error State */}
            {error && (
              <Box ref={errorRef} sx={{ mb: 3 }}>
                <Alert
                  severity="error"
                  onClose={() => setError(null)}
                  icon={<ErrorIcon />}

                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {error}
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Security Notice */}
            {!success && (
              <Alert severity="info" icon={<Lock sx={{ fontSize: '1.2rem', color: '#7A6FA1' }} />} sx={{ mb: 3, bgcolor: '#e0dbf088', }}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  Your card details are secure. Payment is processed by Stripe,
                  PCI DSS certified.
                </Typography>
              </Alert>
            )}

            {/* Payment Form Based on Selected Method */}
            {!success && (
              <Paper
                // ref={paymentFormRef}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: 3,
                  mb: 3,
                  border: '2px solid #e2e8f0',
                }}
              >
                {selectedPaymentMethod === 'card' && (
                  <>
                    {/* Loading state when clientSecret is not ready or initializing */}
                    {!clientSecret || isInitializing ? (
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                          }}
                        >
                          <CreditCard
                            sx={{ fontSize: '1.2rem', color: '#7A6FA1' }}
                          />
                          Card Information
                        </Typography>
                        <Skeleton
                          variant="rectangular"
                          height={56}
                          sx={{ mb: 2, borderRadius: 1 }}
                          animation="wave"

                        />
                        <Skeleton
                          variant="rectangular"
                          height={120}
                          sx={{ mb: 2, borderRadius: 1 }}
                          animation="wave"
                        />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            mt: 3,
                            mb: 2,
                          }}
                        >
                          Billing Address
                        </Typography>
                        <Skeleton
                          variant="rectangular"
                          height={56}
                          sx={{ mb: 2, borderRadius: 1 }}
                          animation="wave"
                        />
                        <Skeleton
                          variant="rectangular"
                          height={56}
                          sx={{ mb: 2, borderRadius: 1 }}
                          animation="wave"
                        />
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Skeleton
                              variant="rectangular"
                              height={56}
                              sx={{ borderRadius: 1 }}
                              animation="wave"
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Skeleton
                              variant="rectangular"
                              height={56}
                              sx={{ borderRadius: 1 }}
                              animation="wave"
                            />
                          </Grid>
                        </Grid>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            mt: 3,
                            gap: 2,
                          }}
                        >
                          <CircularProgress size={24} />
                          <Typography variant="body2" sx={{ color: 'grey.600' }}>
                            {isInitializing
                              ? 'Setting up your subscription...'
                              : 'Preparing payment form...'}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <>
                        {/* Card Information */}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: '#1e293b',
                          }}
                        >
                          <CreditCard

                            sx={{ fontSize: '1.3rem', color: '#7A6FA1' }}
                          />
                          Card Information
                        </Typography>

                        <TextField
                          fullWidth
                          label="Cardholder Name"
                          value={cardholderName}
                          onChange={e => setCardholderName(e.target.value)}
                          margin="normal"
                          placeholder="Enter cardholder name"
                          disabled={isProcessing}
                          sx={{
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#7A6FA1',
                            },
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '& fieldset': {
                                borderColor: '#7A6FA1',
                              },
                              '&:hover fieldset': {
                                borderColor: '#7A6FA1',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#7A6FA1',
                              },
                            },
                          }}
                        />

                        {/* Stripe Card Element */}
                        <Box
                          sx={{
                            mt: 2,
                            p: 3,
                            border: '2px solid #e2e8f0',
                            borderRadius: 2,
                            bgcolor: 'white',
                            transition: 'border-color 0.2s ease',
                            '&:focus-within': {
                              borderColor: '#7A6FA1',
                            },
                          }}
                        >
                          <CardElement
                            options={CARD_ELEMENT_OPTIONS}
                            onChange={e => setCardComplete(e.complete)}
                          />
                        </Box>

                        {/* Save Card Option */}
                        {/* <FormControlLabel
                          control={
                            <Checkbox
                              checked={saveCard}
                              onChange={e => setSaveCard(e.target.checked)}
                              disabled={isProcessing}
                              sx={{
                                color: '#2E7DAF',
                                '&.Mui-checked': {
                                  color: '#2E7DAF',
                                },
                              }}
                            />
                          }
                          label="Save this card for future payments"
                          sx={{ mt: 2, mb: 2 }}
                        /> */}
                      </>
                    )}
                  </>
                )}

                {/* Amazon Pay Form - Commented out for now */}
                {/* {selectedPaymentMethod === 'amazon_pay' && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#1e293b',
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: '#ff9900',
                        borderRadius: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.6rem',
                      }}
                    >
                      amazon
                    </Box>
                    Amazon Pay
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      You'll be redirected to Amazon to complete your payment
                      securely using your Amazon account.
                    </Typography>
                  </Alert>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      bgcolor: '#fff8f0',
                      borderRadius: 2,
                      border: '2px solid #ff9900',
                      textAlign: 'center',
                    }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: '#ff9900',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          mx: 'auto',
                          mb: 2,
                        }}
                      >
                        amazon
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{ color: '#b45309', fontWeight: 600, mb: 1 }}
                      >
                        Pay with Amazon
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#92400e' }}>
                        Fast, secure checkout using your Amazon account
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 2,
                        mt: 2,
                      }}
                    >
                      <Chip
                        label="✓ Secure"
                        size="small"
                        sx={{ bgcolor: '#fef3c7', color: '#92400e' }}
                      />
                      <Chip
                        label="✓ Fast"
                        size="small"
                        sx={{ bgcolor: '#fef3c7', color: '#92400e' }}
                      />
                      <Chip
                        label="✓ Trusted"
                        size="small"
                        sx={{ bgcolor: '#fef3c7', color: '#92400e' }}
                      />
                    </Box>
                  </Paper>
                </Box>
              )} */}

                {/* Bank Account Form - Commented out for now */}
                {/* {selectedPaymentMethod === 'bank_account' && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: '#1e293b',
                    }}
                  >
                    <AccountBalance sx={{ fontSize: '1.3rem', color: '#10b981' }} />
                    Bank Account Information
                  </Typography>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      Bank transfers typically take 3-5 business days to process and
                      have lower fees than card payments.
                    </Typography>
                  </Alert>

                  <TextField
                    fullWidth
                    label="Account Holder Name"
                    value={bankAccountDetails.accountHolderName}
                    onChange={e =>
                      setBankAccountDetails(prev => ({
                        ...prev,
                        accountHolderName: e.target.value,
                      }))
                    }
                    margin="normal"
                    placeholder="Enter account holder name"
                    disabled={isProcessing}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Routing Number"
                        value={bankAccountDetails.routingNumber}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '') // Only allow digits
                          setBankAccountDetails(prev => ({
                            ...prev,
                            routingNumber: value,
                          }))
                        }}
                        margin="normal"
                        placeholder="9-digit routing number"
                        disabled={isProcessing}
                        inputProps={{ maxLength: 9 }}
                        error={
                          bankAccountDetails.routingNumber.length > 0 &&
                          bankAccountDetails.routingNumber.length !== 9
                        }
                        helperText={
                          bankAccountDetails.routingNumber.length > 0 &&
                          bankAccountDetails.routingNumber.length !== 9
                            ? 'Routing number must be exactly 9 digits'
                            : 'Found on the bottom of your check'
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Account Number"
                        value={bankAccountDetails.accountNumber}
                        onChange={e => {
                          const value = e.target.value.replace(/\D/g, '') // Only allow digits
                          setBankAccountDetails(prev => ({
                            ...prev,
                            accountNumber: value,
                          }))
                        }}
                        margin="normal"
                        placeholder="Account number"
                        disabled={isProcessing}
                        inputProps={{ maxLength: 17 }}
                        error={
                          bankAccountDetails.accountNumber.length > 0 &&
                          bankAccountDetails.accountNumber.length < 4
                        }
                        helperText={
                          bankAccountDetails.accountNumber.length > 0 &&
                          bankAccountDetails.accountNumber.length < 4
                            ? 'Account number must be at least 4 digits'
                            : 'Found on the bottom of your check'
                        }
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>

                  <FormControl component="fieldset" sx={{ mt: 2 }}>
                    <FormLabel
                      component="legend"
                      sx={{ mb: 1, color: '#1e293b', fontWeight: 500 }}
                    >
                      Account Type
                    </FormLabel>
                    <RadioGroup
                      row
                      value={bankAccountDetails.accountType}
                      onChange={e =>
                        setBankAccountDetails(prev => ({
                          ...prev,
                          accountType: e.target.value as 'checking' | 'savings',
                        }))
                      }
                    >
                      <FormControlLabel
                        value="checking"
                        control={
                          <Radio
                            sx={{
                              color: '#10b981',
                              '&.Mui-checked': { color: '#10b981' },
                            }}
                          />
                        }
                        label="Checking"
                      />
                      <FormControlLabel
                        value="savings"
                        control={
                          <Radio
                            sx={{
                              color: '#10b981',
                              '&.Mui-checked': { color: '#10b981' },
                            }}
                          />
                        }
                        label="Savings"
                      />
                    </RadioGroup>
                  </FormControl>

                  <Alert severity="success" sx={{ mt: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                      🔒 Your bank account is secure
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                      • Bank-level encryption protects your information
                      <br />
                      • We never store your full account details
                      <br />
                      • Payments are processed through secure banking networks
                      <br />• You can cancel or modify payments anytime
                    </Typography>
                  </Alert>

                  <Paper
                    elevation={0}
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: 2,
                    }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                    >
                      <Info sx={{ color: '#10b981', fontSize: '1.1rem' }} />
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#065f46' }}
                      >
                        Processing Timeline
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: '#047857', fontSize: '0.85rem' }}
                    >
                      Bank transfers typically take 3-5 business days to complete.
                      You'll receive email updates on the payment status.
                    </Typography>
                  </Paper>
                </Box>
              )} */}
              </Paper>
            )}

            {/* Billing Address Section - Only for Card Payments */}
            {!success && selectedPaymentMethod === 'card' && clientSecret && (
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  p: 3,
                  mb: 3,
                  border: '2px solid #ded8f4c4',
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: '#1e293b',
                  }}
                >
                  Billing Address
                </Typography>

                <Box sx={{ mt: 2, mb: 1 }}>
                  <AddressAutocomplete
                    sx={{
                      margin: 'normal',
                      fullWidth: true,
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: '#7A6FA1',
                      },
                      '& .MuiInputBase-root': {
                        height: 56,
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '16px !important' as any,
                        '& fieldset': {
                          borderColor: '#ded8f4c4',
                        },
                        '&:hover fieldset': {
                          borderColor: '#7A6FA1',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#7A6FA1',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: '1.5rem',
                        fontSize: '1rem',
                      },
                    }}
                    value={billingAddress.address1}
                    onChange={(address, components) => {
                      setBillingAddress(prev => ({
                        ...prev,
                        address1: address,
                        // Only update fields if components are provided and have values
                        // Don't clear them when address is cleared (components will have empty strings)
                        ...(components && {
                          ...(components.addressLine2 && {
                            address2: components.addressLine2,
                          }),
                          ...(components.city && {
                            city: components.city,
                          }),
                          ...(components.state && {
                            state: components.state,
                          }),
                          ...((components.postalCode || components.zipCode) && {
                            postalCode:
                              components.postalCode || components.zipCode,
                          }),
                        }),
                      }))
                    }}
                    placeholder="Start typing billing address..."
                    label="Address Line 1"
                    required
                    disabled={isProcessing}
                    addressType="billingAddress"
                    onAddressSelect={components => {
                      setBillingAddress(prev => ({
                        ...prev,
                        address2: components.addressLine2 || prev.address2,
                        city: components.city || prev.city,
                        state: components.state || prev.state,
                        postalCode:
                          components.postalCode ||
                          components.zipCode ||
                          prev.postalCode,
                      }))
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  name="address2"
                  value={billingAddress.address2}
                  onChange={handleAddressChange}
                  margin="normal"
                  disabled={isProcessing}
                  sx={{
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#7A6FA1',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#ded8f4c4',
                      },
                      '&:hover fieldset': {
                        borderColor: '#7A6FA1',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#7A6FA1',
                      },
                    },
                  }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={billingAddress.city}
                      onChange={handleAddressChange}
                      margin="normal"
                      disabled={isProcessing}
                      sx={{
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#7A6FA1',
                        },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: '#ded8f4c4',
                          },
                          '&:hover fieldset': {
                            borderColor: '#7A6FA1',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#7A6FA1',
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={billingAddress.state}
                      onChange={handleAddressChange}
                      margin="normal"
                      disabled={isProcessing}
                      sx={{
                        '& .MuiInputLabel-root.Mui-focused': {
                          color: '#7A6FA1',
                        },
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          '& fieldset': {
                            borderColor: '#ded8f4c4',
                          },
                          '&:hover fieldset': {
                            borderColor: '#7A6FA1',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#7A6FA1',
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Postal Code"
                  name="postalCode"
                  value={billingAddress.postalCode}
                  onChange={handleAddressChange}
                  margin="normal"
                  disabled={isProcessing}
                  sx={{
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#7A6FA1',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': {
                        borderColor: '#ded8f4c4',
                      },
                      '&:hover fieldset': {
                        borderColor: '#7A6FA1',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#7A6FA1',
                      },
                    },
                  }}
                />
              </Paper>
            )}

            {/* Action Buttons */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                p: 3,
                border: '2px solid #ded8f4c4',
                bgcolor: '#fafafa',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {!success && (
                  <>
                    <Button
                      onClick={onClose}
                      disabled={isProcessing}
                      variant="outlined"
                      sx={{
                        color: '#7A6FA1',
                        borderColor: '#ded8f4c4',
                        '&:hover': {
                          borderColor: '#7A6FA1',
                          bgcolor: '#ded8f4c4',
                        },
                        minWidth: 100,
                        borderRadius: 2,
                      }}
                    >
                      Cancel
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {/* Payment Method Indicator */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCard
                          sx={{ color: '#7A6FA1', fontSize: '1.2rem' }}
                        />
                        <Typography variant="body2" sx={{ color: '#7A6FA1' }}>
                          Card Payment
                        </Typography>
                      </Box>

                      <Button
                        onClick={handlePayment}
                        variant="contained"
                        disabled={
                          isProcessing ||
                          (selectedPaymentMethod === 'card' &&
                            (!clientSecret || !stripe))
                        }
                        sx={{
                          bgcolor: '#7A6FA1',
                          '&:hover': {
                            bgcolor: '#7A6FA1',
                          },
                          minWidth: 160,
                          borderRadius: 2,
                          py: 1.5,
                          fontSize: '1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                      >
                        {isProcessing ? (
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                          >
                            <CircularProgress size={20} sx={{ color: 'white' }} />
                            Processing...
                          </Box>
                        ) : (
                          `Pay $${formattedPlanAmount}`
                        )}
                      </Button>
                    </Box>
                  </>
                )}

                {success && (
                  <Box sx={{ width: '100%', textAlign: 'center' }}>
                    {!isFromOnboarding ? (
                      <Button
                        onClick={() => {
                          if (completedPaymentIntentId) {
                            onPaymentSuccess(completedPaymentIntentId)
                          }
                          onClose()
                        }}
                        variant="contained"
                        sx={{
                          bgcolor: '#7A6FA1',
                          '&:hover': { bgcolor: '#7A6FA1' },
                          minWidth: 250,
                          borderRadius: 2,
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                      >
                        Continue to Bank Setup →
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          if (completedPaymentIntentId) {
                            onPaymentSuccess(completedPaymentIntentId)
                          }
                          onClose()
                        }}
                        variant="contained"
                        sx={{
                          bgcolor: '#10b981',
                          '&:hover': { bgcolor: '#0ea676' },
                          minWidth: 250,
                          borderRadius: 2,
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                        }}
                      >
                        Continue →
                      </Button>
                    )}

                  </Box>
                )}

              </Box>
              {success &&
                <Box
                  sx={{
                    mt: 3,
                    p: 2,
                    borderRadius: 2.5,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: '#f1f5f9',
                      borderColor: '#cbd5e1',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: '#ded8f4c4',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Info sx={{ color: '#7A6FA1', fontSize: '1.1rem' }} />
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#7A6FA1',
                      fontWeight: 500,
                      lineHeight: 1.5,
                    }}
                  >
                    To ensure timely and secure payment for your services, please
                    connect your Stripe account. This will enable direct deposits
                    to your bank account for completed sessions.
                  </Typography>

                </Box>
              }

              {/* Trust Indicators */}
              {!success && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: 3,
                    mt: 3,
                    pt: 2,
                    borderTop: '1px solid #e2e8f0',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Security sx={{ color: '#10b981', fontSize: '1rem' }} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      SSL Secured
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Verified sx={{ color: '#10b981', fontSize: '1rem' }} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      PCI Compliant
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Lock sx={{ color: '#10b981', fontSize: '1rem' }} />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      Bank-Level Security
                    </Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </>
    )
  }

// Main wrapper component with Stripe Elements provider
export const AgencyPaymentModal: React.FC<PaymentModalProps> = props => {
  const { open, onClose, isInitializing = false } = props
  const [error, setError] = useState<string | null>(null)

  return (
    <Dialog
      open={open}
      onClose={isInitializing ? undefined : onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          pb: 2,
          pt: 3,
          position: 'relative',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                bgcolor: '#7A6FA1',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Lock sx={{ color: 'white', fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: '#1e293b' }}
              >
                Secure Payment
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                Choose your preferred payment method
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            disabled={isInitializing}
            sx={{
              color: '#c5bfddb3',
              '&:hover': {
                bgcolor: '#7A6FA1',
              },
              '&.Mui-disabled': {
                color: '#7a6fa14b',
              },
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 3 }}>
            {error}
          </Alert>
        )} */}
        <Elements stripe={stripePromise}>
          <PaymentForm {...props} onError={setError} />
        </Elements>
      </DialogContent>
    </Dialog>
  )
}

export default AgencyPaymentModal
